import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { View, Pressable } from "react-native"
import { router } from "expo-router"
import { createExpenseInput } from "@white-labels/input/src/m5azen"
import { TextInput, Text, Button } from "@src/components/core"
import { ExpenseCategoryPicker } from "@src/components/expense-category-picker"
import { PriceInput } from "@src/components/price-input"
import { DatePicker } from "@src/components/date-picker"
import { KeyboardAwareScrollView } from "@src/components/keyboard-aware-scroll-view"
import { trpc, dayjs, sharedStyles } from "@src/utils"
import { analytics } from "@src/utils/analytics"
import { ar } from "@src/i18n/ar"

interface ExpenseFormProps {
  id?: number
}

export function ExpenseForm({ id }: ExpenseFormProps) {
  const utils = trpc.useUtils()
  const createExpense = trpc.expense.create.useMutation()
  const editExpense = trpc.expense.edit.useMutation()
  const expense = trpc.expense.get.useQuery({ id: id as number }, { enabled: !!id })
  const isEdit = !!id

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(createExpenseInput),
    defaultValues: {
      amount: expense.data?.amount ? Math.abs(expense.data.amount) : undefined,
      category: expense.data?.categoryId ?? undefined,
      balanceType: expense.data?.balanceType ?? "cash",
      date: expense.data?.date ?? undefined,
      notes: expense.data?.description ?? undefined,
    },
  })

  const { isValid } = formState
  const loading = createExpense.isPending || editExpense.isPending
  const disabled = !isValid || loading

  const onSubmit = handleSubmit((data) => {
    const onSuccess = () => {
      utils.expense.list.invalidate()
      analytics.logEvent({ name: "expense_created", params: { amount: data.amount, category: String(data.category) } })
      router.back()
    }

    if (isEdit) {
      editExpense.mutate({ id: id!, ...data }, { onSuccess })
    } else {
      createExpense.mutate(data, { onSuccess })
    }
  })

  return (
    <KeyboardAwareScrollView className="w-full max-w-content mx-auto pt-2" contentContainerStyle={sharedStyles.contentContainer}>
      <Text className="font-bold mb-2">{ar.amount}</Text>
      <Controller
        control={control}
        name="amount"
        render={({ field: { onChange, onBlur, value } }) => (
          <PriceInput className="mb-4" value={value ? Number(value) : undefined} onValueChange={onChange} onBlur={onBlur} autoFocus={!isEdit} selectTextOnFocus />
        )}
      />

      <Text className="font-bold mb-2">{ar.category}</Text>
      <Controller control={control} name="category" render={({ field: { onChange, value } }) => <ExpenseCategoryPicker id={value} onChange={onChange} className="mb-4" />} />

      {!isEdit && (
        <>
          <Text className="mb-2">{ar.paymentSource}</Text>
          <Controller
            control={control}
            name="balanceType"
            render={({ field: { onChange, value } }) => (
              <View className="flex-row gap-x-2 mb-2">
                {(["cash", "visa", "wallet"] as const).map((bal) => (
                  <Pressable key={bal} onPress={() => onChange(bal)} className={`flex-1 py-2 items-center rounded ${value === bal ? "bg-blue-500" : "bg-gray-200"}`}>
                    <Text className={value === bal ? "text-white" : "text-black"}>{ar[bal]}</Text>
                  </Pressable>
                ))}
              </View>
            )}
          />
        </>
      )}

      <Text className="mb-2">{ar.date}</Text>
      <Controller
        control={control}
        name="date"
        render={({ field }) => (
          <DatePicker
            className="mb-4"
            value={field.value ? dayjs.unix(field.value).toDate() : undefined}
            onChange={(date) => field.onChange(date ? dayjs(date).unix() : undefined)}
            mode="datetime"
          />
        )}
      />

      <Text className="mb-2">{ar.notes}</Text>
      <Controller
        control={control}
        name="notes"
        render={({ field: { onChange, onBlur, value } }) => (
          <TextInput className="mb-2" placeholder={ar.addNotePlaceholder} onBlur={onBlur} onChangeText={onChange} value={value} multiline />
        )}
      />

      <Button loading={loading} disabled={disabled} onPress={onSubmit}>
        {ar.save}
      </Button>
    </KeyboardAwareScrollView>
  )
}
